import React from 'react';
import styled, { css } from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import { BodyLink } from 'js/design_system/link/BodyLink';

import { CoScientistCta } from './CoScientistCta';

/* Layout and sections shared by the Co-scientist landing and pricing pages */

export const Page = styled.main`
  background: var(--background-white, #ffffff);
  color: var(--text-primary, #141413);
`;

export const Section = styled.section<{ flush?: boolean; tinted?: boolean }>`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md-48, 48px);
  padding: var(--spacing-lg-80, 80px) var(--spacing-lg-64, 64px);
  scroll-margin-top: var(--spacing-lg-80, 80px);
  text-align: center;

  &.tinted {
    background: var(--background-beige-light, #f9f8f6);
  }

  @include breakpoint(medium) {
    padding: var(--spacing-lg-64, 64px) var(--spacing-md-40, 40px);
  }

  @include breakpoint(small) {
    gap: var(--spacing-md-40, 40px);
    padding: var(--spacing-md-40, 40px) var(--spacing-sm-24, 24px);
  }

  @media (max-width: 991px) {
    &.flush {
      padding-left: 0;
      padding-right: 0;
    }
  }
`;

export const SecondaryText = styled(Body)`
  &[class] {
    color: var(--text-secondary, #535366);
  }
`;

export const CtaGroup = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs-16, 16px);
  margin-top: var(--spacing-xs-8, 8px);

  @include breakpoint(small) {
    align-self: stretch;
  }
`;

export const FaqList = styled.div`
  width: 100%;
  max-width: 952px;
  text-align: start;

  /* Accordion titles are buttons, which center text by default */
  button {
    text-align: start;
  }
`;

export const FaqAnswer = styled(Body)`
  &[class] {
    max-width: 800px;
    padding-bottom: var(--spacing-xs-8, 8px);
    color: var(--text-secondary, #535366);
  }
`;

export const displayHeading = css`
  &[class] {
    letter-spacing: var(--type-serif-lg-letter-spacing, -1.5px);
  }
`;

export const semibold = css`
  &[class] {
    font-weight: var(--type-font-weight-600, 600);
  }
`;

export const closingHeading = css`
  &[class] {
    max-width: 600px;
    letter-spacing: var(--type-serif-lg-letter-spacing, -1.5px);
  }
`;

export const Footer = styled.footer`
  @import 'stylesheets/design_system/breakpoint';

  padding: var(--spacing-lg-80, 80px) 115px;
  background: var(--background-black, #141413);
  color: var(--text-white, #ffffff);

  @include breakpoint(medium) {
    padding: var(--spacing-lg-64, 64px) var(--spacing-md-40, 40px);
  }

  @include breakpoint(small) {
    padding: var(--spacing-md-48, 48px) var(--spacing-sm-24, 24px);
  }
`;

export const FooterIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm-32, 32px);
  padding: var(--spacing-md-40, 40px) 0 var(--spacing-lg-80, 80px) 0;
  border-top: 1px solid var(--stroke-white, #ffffff);
  border-bottom: 1px solid var(--stroke-white, #ffffff);
`;

export const FooterLink = styled(BodyLink)`
  &[class] {
    color: inherit;
  }
`;

export const FooterBottom = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
  gap: var(--spacing-sm-24, 24px);
  padding-top: var(--spacing-md-40, 40px);

  @include breakpoint(small) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const FooterLogo = styled.img`
  display: block;
  width: auto;
  height: 18px;
`;

type CtaProps = {
  ctaUrl: string;
  /** Signed in with Co-scientist access, so the CTA opens the app instead of a trial */
  hasAccess: boolean;
};

export const PrimaryCta: React.FC<CtaProps> = ({ ctaUrl, hasAccess }) => (
  <CtaGroup>
    <CoScientistCta href={ctaUrl} withArrow fullWidth>
      {hasAccess ? 'Open Co-scientist' : 'See what Co-scientist can do for you'}
    </CoScientistCta>
    {hasAccess ? null : (
      <SecondaryText size="sm">
        Free to start – No credit card required
      </SecondaryText>
    )}
  </CtaGroup>
);

const closingSection = css`
  &[class] {
    gap: var(--spacing-sm-24, 24px);
  }
`;

export const ClosingCta: React.FC<CtaProps & { heading: string }> = ({
  heading,
  ctaUrl,
  hasAccess,
}) => (
  <Section tinted className={closingSection}>
    <Heading as="h2" fontFamily="serif" size="lg" className={closingHeading}>
      {heading}
    </Heading>
    <PrimaryCta ctaUrl={ctaUrl} hasAccess={hasAccess} />
  </Section>
);

type LandingFooterProps = {
  academiaUrl: string;
  academiaLogoWhite: string;
};

export const LandingFooter: React.FC<LandingFooterProps> = ({
  academiaUrl,
  academiaLogoWhite,
}) => (
  <Footer>
    <FooterIntro>
      <Heading as="h2" fontFamily="serif" size="sm">
        Academia Co-scientist
      </Heading>
      <Body size="lg">
        A proactive, personalized research collaborator.
        <br />
        Powered by{' '}
        <FooterLink href={academiaUrl} size="lg" underline>
          Academia.edu
        </FooterLink>
      </Body>
    </FooterIntro>
    <FooterBottom>
      <FooterLogo src={academiaLogoWhite} alt="Academia" />
      <Body size="md">
        580 California St., Suite 400
        <br />
        San Francisco, CA, 94104
      </Body>
      <Body size="md">
        © {new Date().getFullYear()} Academia. All rights reserved
      </Body>
    </FooterBottom>
  </Footer>
);
